import { Button, Card, useToast } from '@catering/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Save } from 'lucide-react';
import type { JSX, ReactNode } from 'react';
import { useState } from 'react';

import { hasPermission } from '../../../auth/has-permission';
import { useCurrentUser } from '../../../auth/use-current-user';
import type { MenuDetail, MenuUpdateInput, ServiceType } from '../../../menus/api';
import { updateMenu } from '../../../menus/api';
import { formatBasePrice } from '../../../menus/format-base-price';
import type { MenuFormFields, MenuFormValues } from '../menu-form-schema';
import { mapMenuFormError } from '../menu-form-schema';
import { MenuForm } from '../menu-form-page';

export interface MenuInfoTabProps {
  menu: MenuDetail;
  serviceTypes: ServiceType[];
  onRefetch: () => void;
}

/**
 * `menus/spec.md` → "Edición de menú", "Pantallas de alta, edición y
 * detalle en back-office". Mirrors `CustomerDataTab`'s view/edit toggle.
 */
export function MenuInfoTab({ menu, serviceTypes, onRefetch }: MenuInfoTabProps): JSX.Element {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const currentUser = useCurrentUser();
  const readOnly = menu.deletedAt !== null || !hasPermission(currentUser.data, 'menu.update');

  const updateMutation = useMutation({
    mutationFn: (input: MenuUpdateInput) => updateMenu(menu.id, input),
    onSuccess: async () => {
      await invalidate();
      setMode('view');
      toast('success', 'Menú actualizado');
    },
  });

  async function invalidate(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['menu', menu.id] });
    await queryClient.invalidateQueries({ queryKey: ['menus'] });
    onRefetch();
  }

  if (mode === 'edit') {
    const { formError, fieldError } = mapMenuFormError(updateMutation.error);

    return (
      <Card>
        <MenuForm
          serviceTypes={serviceTypes}
          submitLabel="Guardar cambios"
          submitIcon={Save}
          submitting={updateMutation.isPending}
          {...(formError === undefined ? {} : { formError })}
          {...(fieldError === undefined ? {} : { fieldError })}
          onCancel={() => setMode('view')}
          defaultValues={toFormFields(menu)}
          onSubmit={async (values) => {
            await updateMutation.mutateAsync(diffChanges(menu, values)).catch(() => undefined);
          }}
        />
      </Card>
    );
  }

  const serviceTypeName =
    serviceTypes.find((serviceType) => serviceType.id === menu.serviceTypeId)?.name ?? '—';

  return (
    <Card>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-slate-900">Información del menú</h2>
        {!readOnly ? (
          <Button variant="secondary" size="sm" icon={Pencil} onClick={() => setMode('edit')}>
            Editar
          </Button>
        ) : null}
      </div>

      <dl className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
        <Item label="Código" value={menu.code} />
        <Item label="Nombre" value={menu.name} />
        <Item label="Descripción" value={menu.description ?? '—'} />
        <Item label="Tipo de servicio" value={serviceTypeName} />
        <Item label="Precio base" value={formatBasePrice(menu.basePriceCents)} />
        <Item label="Imagen" value={menu.imageUrl ?? '—'} />
      </dl>
    </Card>
  );
}

function Item({ label, value }: { label: string; value: ReactNode }): JSX.Element {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-1 text-sm font-medium text-slate-900">{value}</dd>
    </div>
  );
}

function toFormFields(menu: MenuDetail): MenuFormFields {
  return {
    code: menu.code,
    name: menu.name,
    description: menu.description ?? '',
    serviceTypeId: menu.serviceTypeId,
    basePrice: (menu.basePriceCents / 100).toFixed(2),
    imageUrl: menu.imageUrl ?? '',
    preparationNotes: menu.preparationNotes ?? '',
  };
}

/** A PATCH sends only the fields that actually changed, same convention as `CustomerDataTab`. */
function diffChanges(menu: MenuDetail, values: MenuFormValues): MenuUpdateInput {
  const changes: MenuUpdateInput = {};

  if (values.code !== menu.code) changes.code = values.code;
  if (values.name !== menu.name) changes.name = values.name;
  if (values.serviceTypeId !== menu.serviceTypeId) changes.serviceTypeId = values.serviceTypeId;
  if (values.basePrice !== menu.basePriceCents) changes.basePriceCents = values.basePrice;

  const newDescription = values.description === '' ? null : values.description;
  if (newDescription !== menu.description) changes.description = newDescription;

  const newImageUrl = values.imageUrl === '' ? null : values.imageUrl;
  if (newImageUrl !== menu.imageUrl) changes.imageUrl = newImageUrl;

  return changes;
}
